import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  type CountryCode,
} from 'libphonenumber-js/min';
import { useGeoCountry } from '../../hooks/useGeoCountry';
import { getAttribution } from '../../lib/attribution';
import { trackMarketingEvent } from '../../lib/marketingEvents';
import { DEMO_URL } from '../../data/links';

const LEAD_API = import.meta.env.VITE_LEAD_API_URL || 'https://api.geofleet.ai/website/lead';
export const CONTACT_EMAIL = 'care@geofleet.ai';

const FLEET_SIZES = [
  { key: 'small', label: '1–10' },
  { key: 'medium', label: '11–50' },
  { key: 'large', label: '51–100' },
  { key: 'xl', label: '101–500' },
  { key: 'xxl', label: '500+' },
] as const;
const INQUIRY_TYPES = ['general', 'demo', 'pricing', 'support', 'partnership', 'enterprise'];

// Keys must match geo-user's SOLUTIONS (HubSpot `solutions_interested` options).
const SOLUTIONS = [
  { key: 'route_planning', icon: 'ri-route-line' },
  { key: 'shipment_tracking', icon: 'ri-map-pin-time-line' },
  { key: 'fleet_management', icon: 'ri-truck-line' },
  { key: 'gps_tracking', icon: 'ri-gps-line' },
  { key: 'auto_dispatch', icon: 'ri-flow-chart' },
  { key: 'proof_of_delivery', icon: 'ri-camera-line' },
  { key: 'driver_app', icon: 'ri-smartphone-line' },
  { key: 'customer_notifications', icon: 'ri-whatsapp-line' },
  { key: 'dashcam_safety', icon: 'ri-vidicon-line' },
  { key: 'cash_on_delivery', icon: 'ri-money-dollar-circle-line' },
  { key: 'inventory_hubs', icon: 'ri-store-2-line' },
  { key: 'ai_agents', icon: 'ri-robot-2-line' },
  { key: 'analytics', icon: 'ri-bar-chart-2-line' },
  { key: 'integrations', icon: 'ri-plug-line' },
] as const;
const SOLUTION_KEYS: string[] = SOLUTIONS.map((s) => s.key);
const JOB_TITLE_KEYS = ['founder', 'operations', 'fleet', 'logistics', 'dispatch', 'it', 'procurement'] as const;

const COUNTRIES = getCountries();
const englishNames = new Intl.DisplayNames(['en'], { type: 'region' });

function flag(code: string) {
  return String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0)));
}

function isCountry(code: string | null | undefined): code is CountryCode {
  return !!code && (COUNTRIES as string[]).includes(code);
}

function browserCountry(): CountryCode | null {
  try {
    const region = new Intl.Locale(navigator.language).maximize().region;
    return isCountry(region) ? region : null;
  } catch {
    return null;
  }
}

const inputClass =
  'w-full h-11 px-3.5 rounded-lg border border-gray-300 bg-white text-[15px] text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/10 outline-none transition';

function Field({ id, label, optional, children }: { id: string; label: string; optional?: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="flex items-baseline justify-between text-sm font-medium text-gray-800 mb-1.5">
        {label}
        {optional && <span className="text-xs font-normal text-gray-400">{optional}</span>}
      </label>
      {children}
    </div>
  );
}

export default function ContactForm() {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { countryCode: ipCountry } = useGeoCountry();
  const openedAt = useRef(Date.now());

  const [form, setForm] = useState({ name: '', email: '', company: '', jobTitle: '', phone: '', fleetSize: '', message: '', nickname: '' });
  // `?solution=route_planning,gps_tracking` pre-selects solutions (e.g. from a feature page CTA).
  const [solutions, setSolutions] = useState<string[]>(() =>
    (searchParams.get('solution') ?? '').split(',').filter((k) => SOLUTION_KEYS.includes(k))
  );
  const toggleSolution = (key: string) =>
    setSolutions((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const [country, setCountry] = useState<CountryCode>(() => browserCountry() ?? 'US');
  const countryTouched = useRef(false);
  const [errors, setErrors] = useState<Partial<Record<'name' | 'email' | 'phone', string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // IP lookup wins over the browser locale, unless the visitor already picked a country.
  useEffect(() => {
    const code = ipCountry?.toUpperCase();
    if (!countryTouched.current && isCountry(code)) setCountry(code);
  }, [ipCountry]);

  const countryOptions = useMemo(() => {
    let names: Intl.DisplayNames;
    try {
      names = new Intl.DisplayNames([i18n.language, 'en'], { type: 'region' });
    } catch {
      names = englishNames;
    }
    return COUNTRIES.map((code) => ({ code, name: names.of(code) ?? code })).sort((a, b) =>
      a.name.localeCompare(b.name, i18n.language)
    );
  }, [i18n.language]);

  const dialCode = `+${getCountryCallingCode(country)}`;

  const set = (name: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name in errors) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onPhoneChange = (value: string) => {
    // Typing a +code switches the country; otherwise format in the selected country's style.
    const formatter = new AsYouType(country);
    const formatted = formatter.input(value);
    const detected = formatter.getCountry();
    if (value.startsWith('+') && detected) {
      // Move the +code into the prefix and keep the national part in the input.
      countryTouched.current = true;
      setCountry(detected);
      const national = value.replace(/\D/g, '').slice(getCountryCallingCode(detected).length);
      set('phone', new AsYouType(detected).input(national));
      return;
    }
    // Don't fight backspace over a formatting character.
    set('phone', value.length < form.phone.length ? value : formatted);
  };

  const onCountryChange = (code: CountryCode) => {
    countryTouched.current = true;
    setCountry(code);
    if (form.phone && !form.phone.startsWith('+')) set('phone', new AsYouType(code).input(form.phone));
  };

  const validate = () => {
    const next: typeof errors = {};
    if (!form.name.trim()) next.name = t('contact.form.errors.name');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) next.email = t('contact.form.errors.email');
    const phone = parsePhoneNumberFromString(form.phone, country);
    if (!phone?.isValid()) next.phone = t('contact.form.errors.phone', { country: englishNames.of(country) });
    setErrors(next);
    return Object.keys(next).length === 0 ? phone! : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const phone = validate();
    if (!phone) return;

    const [firstName, ...rest] = form.name.trim().split(/\s+/);
    const inquiry = searchParams.get('subject');
    const phoneCountry = phone.country ?? country;
    const payload = {
      email: form.email.trim(),
      first_name: firstName,
      last_name: rest.join(' '),
      company: form.company.trim(),
      job_title: form.jobTitle.trim(),
      solutions,
      phone_number: phone.number,
      country: englishNames.of(phoneCountry) ?? phoneCountry,
      fleet_size: form.fleetSize,
      inquiry_type: inquiry && INQUIRY_TYPES.includes(inquiry) ? inquiry : '',
      message: form.message.trim(),
      page_url: window.location.href,
      page_title: document.title,
      language: i18n.language,
      ...getAttribution(),
      nickname: form.nickname,
      elapsed_ms: Date.now() - openedAt.current,
    };

    setSubmitting(true);
    try {
      const res = await fetch(LEAD_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        // 400 carries a readable validation message; anything else stays generic.
        const body = await res.json().catch(() => null);
        throw new Error(res.status === 400 && body?.message ? body.message : '');
      }
      trackMarketingEvent('generate_lead', {
        form: 'contact',
        fleet_size: form.fleetSize || undefined,
        country: phoneCountry,
        solutions: solutions.join(',') || undefined,
      });
      setSubmitted(true);
      // The success card is shorter than the form; bring it back into view.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError((err instanceof Error && err.message) || t('contact.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <i className="ri-check-line text-2xl text-emerald-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('contact.form.successTitle', { name: form.name.trim().split(/\s+/)[0] })}</h2>
        <p className="text-gray-600 mb-6 max-w-sm mx-auto">{t('contact.form.successBody')}</p>
        <a
          href={DEMO_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 h-11 px-5 rounded-lg text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 transition"
        >
          <i className="ri-calendar-line" />
          {t('contact.form.bookNow')}
        </a>
      </div>
    );
  }

  const errorText = (msg?: string) => msg && <p className="mt-1.5 text-xs text-red-600">{msg}</p>;

  return (
    <form onSubmit={handleSubmit} noValidate className="relative space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="name" label={t('contact.form.name')}>
          <input
            id="name"
            autoComplete="name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            className={inputClass}
            aria-invalid={!!errors.name}
          />
          {errorText(errors.name)}
        </Field>
        <Field id="email" label={t('contact.form.email')}>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => set('email', e.target.value)}
            className={inputClass}
            placeholder="you@company.com"
            aria-invalid={!!errors.email}
          />
          {errorText(errors.email)}
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="company" label={t('contact.form.company')} optional={t('contact.form.optional')}>
          <input
            id="company"
            autoComplete="organization"
            value={form.company}
            onChange={(e) => set('company', e.target.value)}
            className={inputClass}
          />
        </Field>
        <Field id="jobTitle" label={t('contact.form.jobTitle')} optional={t('contact.form.optional')}>
          <input
            id="jobTitle"
            list="job-title-options"
            autoComplete="organization-title"
            value={form.jobTitle}
            onChange={(e) => set('jobTitle', e.target.value)}
            className={inputClass}
            placeholder={t('contact.form.jobTitlePlaceholder')}
          />
          <datalist id="job-title-options">
            {JOB_TITLE_KEYS.map((k) => (
              <option key={k} value={t(`contact.form.jobTitles.${k}`)} />
            ))}
          </datalist>
        </Field>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="country" label={t('contact.form.country')}>
          <select
            id="country"
            autoComplete="country"
            value={country}
            onChange={(e) => onCountryChange(e.target.value as CountryCode)}
            className={`${inputClass} pr-8`}
          >
            {countryOptions.map((c) => (
              <option key={c.code} value={c.code}>
                {flag(c.code)} {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field id="phone" label={t('contact.form.phone')}>
          <div
            dir="ltr"
            className={`flex h-11 rounded-lg border bg-white transition focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-600/10 ${
              errors.phone ? 'border-red-400' : 'border-gray-300'
            }`}
          >
            <span className="flex items-center gap-1.5 pl-3.5 pr-3 border-r border-gray-200 text-[15px] text-gray-700 whitespace-nowrap select-none">
              <span aria-hidden="true">{flag(country)}</span>
              {dialCode}
            </span>
            <input
              id="phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel-national"
              value={form.phone}
              onChange={(e) => onPhoneChange(e.target.value)}
              className="flex-1 min-w-0 px-3 bg-transparent text-[15px] text-gray-900 placeholder:text-gray-400 outline-none rounded-r-lg"
              aria-invalid={!!errors.phone}
            />
          </div>
          {errorText(errors.phone)}
        </Field>
      </div>

      <fieldset>
        <legend className="flex items-baseline justify-between w-full text-sm font-medium text-gray-800 mb-2">
          {t('contact.form.solutions')}
          <span className="text-xs font-normal text-gray-400">{t('contact.form.pickAny')}</span>
        </legend>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {SOLUTIONS.map((s) => {
            const active = solutions.includes(s.key);
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={active}
                onClick={() => toggleSolution(s.key)}
                className={`flex items-center gap-2.5 h-10 px-3 rounded-lg border text-left text-sm transition ${
                  active
                    ? 'border-blue-600 bg-blue-50 text-blue-800 font-medium'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${s.icon} text-base ${active ? 'text-blue-600' : 'text-gray-400'}`} />
                <span className="flex-1 truncate">{t(`contact.form.solutionOptions.${s.key}`)}</span>
                {active && <i className="ri-check-line text-blue-600" />}
              </button>
            );
          })}
        </div>
      </fieldset>

      <fieldset>
        <legend className="text-sm font-medium text-gray-800 mb-1.5">{t('contact.form.fleetSize')}</legend>
        <div className="flex flex-wrap gap-2">
          {FLEET_SIZES.map((s) => {
            const active = form.fleetSize === s.key;
            return (
              <button
                key={s.key}
                type="button"
                aria-pressed={active}
                onClick={() => set('fleetSize', active ? '' : s.key)}
                className={`h-9 px-3.5 rounded-full border text-sm font-medium transition ${
                  active
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <Field id="message" label={t('contact.form.message')} optional={t('contact.form.optional')}>
        <textarea
          id="message"
          rows={3}
          value={form.message}
          onChange={(e) => set('message', e.target.value)}
          className={`${inputClass} h-auto py-2.5 resize-none`}
          placeholder={t('contact.form.messagePlaceholder')}
        />
      </Field>

      <div aria-hidden="true" className="absolute -left-[10000px] w-px h-px overflow-hidden">
        <label htmlFor="nickname">Nickname</label>
        <input
          id="nickname"
          tabIndex={-1}
          autoComplete="off"
          value={form.nickname}
          onChange={(e) => set('nickname', e.target.value)}
        />
      </div>

      {submitError && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
          {submitError}{' '}
          {t('contact.form.orEmail')}{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold underline">
            {CONTACT_EMAIL}
          </a>
        </div>
      )}

      <button
        type="submit"
        disabled={submitting}
        className="w-full h-12 rounded-lg text-[15px] font-semibold text-white bg-blue-600 hover:bg-blue-700 transition disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
      >
        {submitting && <i className="ri-loader-4-line animate-spin" />}
        {t('contact.form.submit')}
      </button>
      <p className="text-xs text-gray-500 text-center">{t('contact.form.reassurance')}</p>
    </form>
  );
}
